export const PLACEHOLDER_IMAGE = "/images/Placeholder.svg";

const DEFAULT_BUCKET_URL = process.env.NEXT_PUBLIC_BUCKET_URL || "";

export interface GetImageUrlOptions {
  bucketUrl?: string;
  placeholder?: string;
  allowEmpty?: boolean;
}

export const getBucketUrl = (): string => DEFAULT_BUCKET_URL;

type ImageLike = { url?: string; isCover?: boolean };

const resolveOptions = (
  bucketUrlOrOptions?: string | GetImageUrlOptions,
  options?: GetImageUrlOptions
): GetImageUrlOptions => {
  if (typeof bucketUrlOrOptions === "string") {
    return { bucketUrl: bucketUrlOrOptions, ...options };
  }
  return { ...bucketUrlOrOptions, ...options };
};

export const getImageUrl = (
  url: string | undefined | null,
  bucketUrlOrOptions?: string | GetImageUrlOptions,
  options?: GetImageUrlOptions
): string => {
  const opts = resolveOptions(bucketUrlOrOptions, options);
  const bucketUrl = opts.bucketUrl ?? DEFAULT_BUCKET_URL;
  const placeholder = opts.placeholder ?? PLACEHOLDER_IMAGE;

  const trimmed = url?.trim();
  if (!trimmed) {
    return opts.allowEmpty ? "" : placeholder;
  }

  if (trimmed.startsWith("blob:")) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/images/")) return trimmed;

  const base = bucketUrl.endsWith("/") ? bucketUrl : `${bucketUrl}/`;
  const path = trimmed.startsWith("/") ? trimmed.slice(1) : trimmed;
  return base + path;
};

export const getCoverImageUrl = (
  images?: string | ImageLike | (string | ImageLike)[] | null,
  bucketUrlOrOptions?: string | GetImageUrlOptions,
  options?: GetImageUrlOptions
): string => {
  const opts = resolveOptions(bucketUrlOrOptions, options);

  if (!images) {
    return opts.allowEmpty ? "" : getImageUrl(undefined, opts);
  }

  if (typeof images === "string") {
    return getImageUrl(images, opts);
  }

  if (Array.isArray(images)) {
    if (images.length === 0) {
      return opts.allowEmpty ? "" : getImageUrl(undefined, opts);
    }
    const cover = images.find((img) => typeof img !== "string" && img.isCover) || images[0];
    const url = typeof cover === "string" ? cover : cover?.url;
    return getImageUrl(url, opts);
  }

  return getImageUrl(images.url, opts);
};

export const resolveImageUrls = (
  images: (string | ImageLike)[],
  bucketUrlOrOptions?: string | GetImageUrlOptions
): string[] =>
  images
    .map((img) => {
      const url = typeof img === "string" ? img : img?.url;
      return getImageUrl(url, { ...resolveOptions(bucketUrlOrOptions), allowEmpty: true });
    })
    .filter(Boolean);
